import _ from 'lodash'
import type { Component, BreakpointRange, BaseDataItem } from '@wix/thunderbolt-becky-types'
import type { CompVariantsViewItem, MegaStore, WithOverrides } from '@wix/thunderbolt-catharsis'
import { getChildrenAndSlots } from '@wix/thunderbolt-catharsis'
import type {
	ContainerQueryToSelectorObj,
	MediaQueryToSelectorObj,
	SelectorObj,
	CompIdTo,
	SelectorToCssString as CssString,
} from '../shared.types'
import { getTemplateRepeaterIdSelector, getRegularIdSelector, getInflatedDynamicSlotIdSelector } from './selectorsUtils'
import type { ComponentCss, CssContext } from '../features/componentCss.types'
import { accumulateCssContext } from './accumulateCssContextUtils'
import type { CssFeature, FeatureName } from '../features/cssFeature.types'
import type { DomApplier, SingleComponentCss } from '../features/cssFeatures.types'
import { mapValues } from './mapValues'
import type { CatharsisCssResult } from '../types'

export type IDomApplierProvider = <TFeatureName extends FeatureName>(
	featureName: TFeatureName
) => DomApplier<TFeatureName>

const EMPTY_ARRAY = [] as const
export const THE_DEFAULT_BREAKPOINT = { id: 'default', mediaQuery: 'default' } as const

const isBreakpointRange = (item: BaseDataItem): item is BreakpointRange => item.type === 'BreakpointRange'
export const toMediaQueryWithId = (item: BreakpointRange) => ({ id: item.id, mediaQuery: toMediaQuery(item) })

export const toMediaQuery = (item: BreakpointRange) => {
	const min = item.min ? ` and (min-width: ${item.min}px)` : ''
	const max = item.max ? ` and (max-width: ${item.max}px)` : ''
	return `@media screen${min}${max}`
}

const selectorObjToCss = (
	selectorObj: ContainerQueryToSelectorObj | SelectorObj | CssString,
	insideScopedRule: boolean = false
): string =>
	Object.entries(selectorObj)
		.flatMap(([selector, css]) => {
			if (_.isEmpty(css)) {
				return ''
			}

			const updatedSelector = selector.includes('(.masterPage)') ? selector.replace('(.masterPage)', '(.masterPage, .POPUPS_ROOT)') : selector

			// In case we are in stylable component
			if (typeof css === 'string') {
				return css
			} else if (!insideScopedRule && (updatedSelector.startsWith('@container') || updatedSelector.startsWith('@supports'))) {
				// In case we are ContainerQueryToSelectorObj
				return `${updatedSelector}{${selectorObjToCss(css, true)}}`
			} else {
				// In case we are selectorObj
				return `${updatedSelector}{${Object.entries(css)
					.map(([key, value]) => `${key}:${value};`)
					.join('')}}`
			}
		})
		.join('')

export function createComponentTraversal(
	getCompById: (compId: string) => ComponentCss,
	compCssMappersGetByIdWithOverrides: <T>(id: string) => WithOverrides<T>,
	isMobileView: boolean,
	domApplierProvider: <TFeatureName extends FeatureName>(featureName: TFeatureName) => DomApplier<TFeatureName>,
	structureStoreGetById: (id: string) => Component
) {
	const traverseComponents = (
		comp: Component,
		ancestorBreakpointsOrder: ComponentCss['breakpointsOrder'],
		acc: CompIdTo<SingleComponentCss>,
		cssContext: CssContext
	) => {
		const compId = comp.id
		const { breakpointsOrder = ancestorBreakpointsOrder, compSpecificData, ...compCss } = getCompById(compId)
		// Remove this once repeater is handled in all cases
		if (compCss && compCss.compCssMappers) {
			compCss.compCssMappers.compCssMappers = compCssMappersGetByIdWithOverrides(compId)
		}

		const getParent = () => ({ getParent: cssContext?.getParent, componentId: compId })
		const accumulatedCssContext = accumulateCssContext(
			comp.id,
			{ ...cssContext, getParent, isMobileView },
			compSpecificData
		)

		acc[compId] = getSingleComponentCss(comp, breakpointsOrder, accumulatedCssContext, compCss, domApplierProvider)

		accumulatedCssContext.isInRepeater = accumulatedCssContext.isInRepeater || comp.type === 'RepeaterContainer'

		accumulatedCssContext.isSlot = accumulatedCssContext.isSlot || !!comp.slotsQuery

		getChildrenAndSlots(comp).forEach((childId) =>
			traverseComponents(structureStoreGetById(childId), breakpointsOrder, acc, accumulatedCssContext)
		)
		return acc
	}
	return traverseComponents
}

export const getComponentsCss = (
	megaStore: MegaStore,
	pageCompId: string,
	isMobileView: boolean,
	domApplierProvider: IDomApplierProvider
): CompIdTo<SingleComponentCss> => {
	const cssStore = megaStore.getChildStore('componentCss')
	const compCssMappersStore = megaStore.getChildStore('compCssMappers')
	const structureStore = megaStore.getChildStore('structure')

	const traverseComponents = createComponentTraversal(
		(compId) => cssStore.getById(compId),
		(compId) => compCssMappersStore.getByIdWithOverrides(compId),
		isMobileView,
		domApplierProvider,
		(compId) => structureStore.getById(compId)
	)

	return traverseComponents(structureStore.getById(pageCompId), undefined, {}, {})
}

export const getMediaQueriesFromVariants = (variants: CompVariantsViewItem | null | undefined) =>
	_.filter(variants, isBreakpointRange).map(toMediaQueryWithId)

export const getIdSelectorFromCompIdAndCssContext = (compId: string, cssContext: CssContext) => {
	// @ts-ignore
	const isDynamicSlotExperimentOpen = globalThis.isDynamicSlotExperimentOpen

	return cssContext.isInRepeater
		? getTemplateRepeaterIdSelector(compId)
		: isDynamicSlotExperimentOpen && cssContext.isSlot
			? getInflatedDynamicSlotIdSelector(compId)
			: getRegularIdSelector(compId)
}

export const getSingleComponentCss = (
	component: Component,
	breakpointsOrder: ComponentCss['breakpointsOrder'],
	cssContext: CssContext,
	compCss: Omit<ComponentCss, 'breakpointsOrder' | 'compSpecificData'>,
	domApplierProvider: IDomApplierProvider
): SingleComponentCss => {
	const compId = component.id
	const idSelector = getIdSelectorFromCompIdAndCssContext(compId, cssContext)

	const declaredMediaQueries = [
		THE_DEFAULT_BREAKPOINT,
		...(breakpointsOrder?.values || EMPTY_ARRAY).map(toMediaQueryWithId),
	]

	const breakpoints = _.uniqBy(
		[
			...declaredMediaQueries,
			// this is because old editorX sites have css on breakpoint that doesn't exist in the breakpointsOrder
			...getMediaQueriesFromVariants(compCss.responsiveLayout?.layoutVariants),
			...getMediaQueriesFromVariants(compCss.variables?.variablesVariants),
		],
		'id'
	)

	return mapValues(compCss, (css, key) => {
		if (!css) {
			return {}
		}

		return breakpoints.reduce<MediaQueryToSelectorObj>((acc, { id, mediaQuery }) => {
			const selectorObj = domApplierProvider(key)(component, idSelector, id, css as any, cssContext)
			if (!_.isEmpty(selectorObj)) {
				acc[mediaQuery] = acc[mediaQuery] ? _.merge({}, acc[mediaQuery], selectorObj) : selectorObj
			}
			return acc
		}, {})
	})
}

export const stringifyMediaQueryToSelectorObj = (
	mediaQueryToSelectorObj: MediaQueryToSelectorObj,
	parentMediaRule: CssFeature<any, any>['parentMediaRule']
) => {
	let css = ''
	for (const mediaQuery in mediaQueryToSelectorObj) {
		const selectorObj = mediaQueryToSelectorObj[mediaQuery]
		const cssString = selectorObjToCss(selectorObj)
		if (cssString) {
			css += mediaQuery === 'default' ? cssString : `${mediaQuery}{${cssString}}`
		}
	}
	return parentMediaRule && css ? `@media (${parentMediaRule}) {${css}}` : css
}

const replaceInflationToClasses = (selector: string) => {
	return selector.replace(/_r_/g, ' .')
}

const replaceCompIdWithClassName = (selector: string) => {
	// Capture the groups of:
	// 1. Regular component ID selectors (e.g. #comp-11111 will become .comp-11111)
	// 2. Repeater prefix ID selectors (e.g. [id^="comp-lwxedckp__"] will become .comp-lwxedckp)
	return selector.replace(
		/\[id\^=['"]([^'"]+)__['"]]|#(\b[^\s{]+)/g,
		(m, repeaterIdGroup, idGroup) => `.${idGroup || repeaterIdGroup}`
	)
}

export const styleableInflationCleaning = (styleableInput: string) => {
	const styleableOutput = styleableInput
		// ID selector transformation
		.replace(/#comp-[\w\d]+(?:_r_comp-[\w\d]+)+/g, (match) => {
			return match.replace('#', '.').split('_r_').join(' .')
		})
		// Class selector (ending with -styleId__root) → just keep last part
		.replace(/\.comp-[\w\d]+(?:_r_comp-[\w\d]+)+(?=-styleId__root)/g, (match) => {
			const parts = match.split('_r_')
			return '.' + parts[parts.length - 1]
		})
		// Generic class selector transformation (space-separated)
		.replace(/\.comp-[\w\d]+(?:_r_comp-[\w\d]+)+/g, (match) => {
			return match.split('_r_').join(' .')
		})
	return styleableOutput
}

export const cleanStylableSelectorsStyle = (style: string, presets: Array<string>) => {
	let cleanedStyle = style.replace(
		/\[id\^=['"]([^'"]+)__['"]]|#(comp\b[^\s{]+)/g,
		(m, repeaterIdGroup, idGroup) => `.${idGroup || repeaterIdGroup}`
	)

	cleanedStyle = styleableInflationCleaning(cleanedStyle)

	const presetIdInline = presets.find((preset) => style.includes(preset))
	if (presetIdInline) {
		cleanedStyle = cleanedStyle.replace(new RegExp(`.${presetIdInline}s*`, 'g'), '')
	}
	return cleanedStyle
}

export const sliceCssByPresets = (componentsCss: CatharsisCssResult, presets: Array<string>) => {
	const presetsCss = _.reduce(presets, (acc, preset) => Object.assign(acc, { [preset]: {} }), {
		default: {},
	} as { default: CatharsisCssResult; [preset: string]: CatharsisCssResult })

	_.forEach(componentsCss, (featuresCss, compId) => {
		_.forEach(featuresCss, (mediaQueryCss, cssFeatureName) => {
			_.forEach(mediaQueryCss, (selectors, mediaQuery) => {
				_.forEach(selectors, (style, selector) => {
					const presetId = presets.find((preset) => selector.includes(preset))
					let cleanedStyle = style
					let target = presetsCss.default
					if (presetId) {
						selector = selector.replace(new RegExp(`(?<=^|,)\\s*\\.${presetId}\\s+`, 'g'), '')
						target = presetsCss[presetId]
					}
					const hasStylable = typeof style === 'string'
					if (hasStylable) {
						cleanedStyle = cleanStylableSelectorsStyle(style, presets)
					}
					selector = replaceCompIdWithClassName(selector)
					selector = replaceInflationToClasses(selector)
					_.set(target, [compId, cssFeatureName, mediaQuery, selector], cleanedStyle)
				})
			})
		})
	})
	return presetsCss
}
